import { useEffect, useRef, useState } from 'react';
import { subscribe } from 'lib/pusher';

/**
 * The states every audit page moves through. Queued and running are named apart
 * on purpose — a wait for a worker is not a slow query, and reporting both as
 * "running" makes a busy queue look like a slow report. Pages with more than one
 * phase extend this with their own intermediate states.
 */
export type BaseAuditStatus =
  'idle' | 'queued' | 'running' | 'complete' | 'error';

/** What POST /run answers with. */
export interface AuditRunResponse {
  auditId: string;
  channel: string;
}

/** The shape `useAtomValue` gives back for a mutation atom's loadable. */
export type AuditRunState =
  | { state: 'loading' }
  | { state: 'hasData'; data: AuditRunResponse | null }
  | { state: 'hasError'; error: unknown };

/**
 * One stage of an audit's results.
 *
 * `load` answers whether the payload had landed, so the hook — not the page —
 * owns the status transition. That is what lets a page's loaders be plain data
 * fetches with no knowledge of the lifecycle driving them.
 */
export interface AuditPhase<S extends string> {
  /** The Pusher event announcing this phase. */
  event: string;
  /** Fetches the phase. Resolves false when it has not landed yet. */
  load: (auditId: string) => Promise<boolean>;
  /** The status once it has landed. */
  status: S;
  /**
   * Only advance from a still-pending status. An earlier phase arriving late
   * must not drag a finished audit backwards.
   */
  onlyWhenPending?: boolean;
}

interface Options<S extends string> {
  /** The run atom's loadable, which starts the lifecycle when it resolves. */
  runState: AuditRunState;
  /** The audit's stages, in the order they land. */
  phases: AuditPhase<S>[];
  /** Whether to keep polling in this state. Defaults to queued or running. */
  isPolling?: (status: S) => boolean;
  /**
   * Bind anything beyond the common three and the phases — a page reporting a
   * partial failure, say, where one stage can fail without failing the audit.
   */
  onSubscribe?: (
    subscription: ReturnType<typeof subscribe>,
    auditId: string
  ) => void;
  /** Clear the page's own result state when a fresh audit starts. */
  onStart?: () => void;
  /** How often the fallback re-reads, in ms. Defaults to POLL_INTERVAL_MS. */
  pollIntervalMs?: number;
}

const PENDING: string[] = ['queued', 'running'];
const DEFAULT_IS_POLLING = (status: string) => PENDING.includes(status);

/** How often the fallback re-reads, in ms. */
export const POLL_INTERVAL_MS = 3000;

/**
 * The lifecycle every admin audit page shares: start a run, follow it over
 * Pusher, poll as a fallback, and report which of the two waits it is in.
 *
 * The polling is not belt-and-braces. Pusher pushes are rescued and swallowed in
 * the jobs, and local development usually has no Pusher credentials at all, so
 * without it a dropped event would leave the page waiting on something that
 * never arrives. Either path landing a payload is enough to advance, which is
 * also why `load` reports whether it landed rather than the page assuming it.
 *
 * @param {object} options - The audit's phases and reset behaviour.
 * @return {object} The audit's identity, status, elapsed time and error.
 */
export const useBackgroundAudit = <S extends string = BaseAuditStatus>({
  runState,
  phases,
  isPolling = DEFAULT_IS_POLLING as (status: S) => boolean,
  onSubscribe,
  onStart,
  pollIntervalMs = POLL_INTERVAL_MS,
}: Options<S>) => {
  const [auditId, setAuditId] = useState<string | null>(null);
  const [channel, setChannel] = useState<string | null>(null);
  const [status, setStatus] = useState<S>('idle' as S);
  const [elapsed, setElapsed] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Callers rebuild these every render. Held in a ref so the subscription is
  // keyed to the audit rather than re-bound on each one, which would leak
  // handlers onto the channel.
  const latest = useRef({ phases, isPolling, onSubscribe, onStart });
  latest.current = { phases, isPolling, onSubscribe, onStart };

  // A read that fails is swallowed: the poller will try again in three seconds,
  // and a transient failure must not surface as an unhandled rejection or tear
  // down an audit that is still running.
  const advance = useRef((phase: AuditPhase<S>, id: string) => {
    phase
      .load(id)
      .then((landed) => {
        if (!landed) return;
        setStatus((prev) => {
          const stale = phase.onlyWhenPending && !PENDING.includes(prev);
          return stale ? prev : phase.status;
        });
      })
      .catch(() => undefined);
  });

  useEffect(() => {
    if (!auditId || !channel) return;

    const subscription = subscribe(channel);

    // The job announces itself when a worker actually picks it up, which can be
    // well after the click — this tier's SLA allows several minutes. Until then
    // the audit is queued, not slow.
    subscription.bind('audit_started', () => setStatus('running' as S));
    subscription.bind('audit_complete', (data: { elapsed_seconds: number }) => {
      setElapsed(data.elapsed_seconds);
      setStatus('complete' as S);
    });
    subscription.bind('audit_error', (data: { error: string }) => {
      setError(data?.error ?? 'The audit failed');
      setStatus('error' as S);
    });

    latest.current.phases.forEach((phase) => {
      subscription.bind(phase.event, () => advance.current(phase, auditId));
    });

    latest.current.onSubscribe?.(subscription, auditId);

    return () => {
      subscription.unbind_all?.();
    };
  }, [auditId, channel]);

  useEffect(() => {
    if (!auditId || !latest.current.isPolling(status)) return;

    const interval = setInterval(() => {
      latest.current.phases.forEach((phase) => advance.current(phase, auditId));
    }, pollIntervalMs);

    return () => clearInterval(interval);
  }, [auditId, status, pollIntervalMs]);

  useEffect(() => {
    if (runState.state === 'hasError') {
      setError(
        runState.error instanceof Error
          ? runState.error.message
          : 'Failed to start the audit'
      );
      setStatus('error' as S);
      return;
    }
    if (runState.state !== 'hasData' || !runState.data) return;

    setAuditId(runState.data.auditId);
    setChannel(runState.data.channel);
    setStatus('queued' as S);
    setElapsed(null);
    setError(null);
    latest.current.onStart?.();
  }, [runState]);

  return { auditId, status, elapsed, error };
};
